/**
 * Helpers for itemProgress quizResults: normalize API shapes and scope counts /
 * dialog content to quiz sections that belong to the current progress row
 * (course, program, or microcast).
 */

export function normalizeQuizResultsMap(raw: unknown): Record<string, any> {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
      return {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, any>;
  }
  return {};
}

function collectQuizSectionIdsFromSections(sections: any[] | undefined): Set<string> {
  const ids = new Set<string>();
  const walk = (secs: any[] | undefined) => {
    if (!secs?.length) return;
    for (const sec of secs) {
      if (sec?.type === 'quiz') {
        if (sec.id) ids.add(sec.id);
        if (sec.item?.id) ids.add(sec.item.id);
      }
      if (sec?.sections?.length) walk(sec.sections);
    }
  };
  walk(sections);
  return ids;
}

function findCourseInProgramForQuizKey(program: any, sectionID: string): any {
  const courseSections = program?.filterSections?.('course') ?? [];
  for (const wrap of courseSections) {
    const c = wrap?.item?.course;
    if (c?.getQuizSectionById?.(sectionID)) return c;
  }
  return null;
}

function findProgramQuizSection(program: any, sectionID: string): any {
  const quizzes = program?.filterSections?.('quiz') ?? [];
  return (
    quizzes.find((s: any) => s.id === sectionID || s.item?.id === sectionID)
    ?? null
  );
}

function getQuestionFromProgramQuizSection(
  section: any,
  questionId: string,
): any {
  const questions = section?.item?.questions;
  if (Array.isArray(questions)) {
    return questions.find((q: any) => q?.id === questionId) ?? null;
  }
  return null;
}

/** Quiz result keys that belong to this progress row's content tree. */
export function getFilteredQuizResultKeys(item: any): string[] {
  const map = normalizeQuizResultsMap(item?.quizResults);
  const keys = Object.keys(map);
  if (!keys.length) return [];

  if (item?.course) {
    const c = item.course;
    return keys.filter((k) => !!c.getQuizSectionById?.(k));
  }

  if (item?.program) {
    const allowed = getProgramQuizResultKeyAllowance(item.program);
    return keys.filter((k) => allowed.has(k));
  }

  if (item?.microcast) {
    const allowed = collectQuizSectionIdsFromSections(item.microcast?.sections);
    const filtered = keys.filter((k) => allowed.has(k));
    if (filtered.length === 0 && keys.length > 0 && allowed.size === 0) {
      return keys;
    }
    return filtered;
  }

  return keys;
}

export function getQuizDisplayName(item: any, sectionID: string): string {
  try {
    if (item?.course) {
      return item.course.getQuizSectionById?.(sectionID)?.name ?? 'Deleted';
    }
    if (item?.program) {
      const course = findCourseInProgramForQuizKey(item.program, sectionID);
      if (course) {
        return course.getQuizSectionById?.(sectionID)?.name ?? 'Deleted';
      }
      const pq = findProgramQuizSection(item.program, sectionID);
      if (pq) return pq?.name ?? 'Deleted';
      const microSections = item.program.filterSections?.('microcast') ?? [];
      for (const wrap of microSections) {
        const m = wrap?.item?.microcast;
        const sec = findQuizSectionInNestedSections(m?.sections, sectionID);
        if (sec) return sec?.name ?? 'Deleted';
      }
      return 'Deleted';
    }
    if (item?.microcast) {
      const sec = findQuizSectionInNestedSections(item.microcast?.sections, sectionID);
      return sec?.name ?? 'Deleted';
    }
  } catch {
    return 'Deleted';
  }
  return 'Deleted';
}

function findQuizSectionInNestedSections(sections: any[] | undefined, key: string): any {
  if (!sections?.length) return null;
  for (const sec of sections) {
    if (sec.id === key || sec.item?.id === key) return sec;
    const nested = findQuizSectionInNestedSections(sec.sections, key);
    if (nested) return nested;
  }
  return null;
}

/** Section IDs for all quizzes under a program (courses, program quizzes, nested microcasts). */
function getProgramQuizResultKeyAllowance(program: any): Set<string> {
  const allowed = new Set<string>();
  const fromProgramQuizzes = program.getQuizSections?.() ?? [];
  fromProgramQuizzes.forEach((s: any) => {
    if (s?.id) allowed.add(s.id);
    if (s?.item?.id) allowed.add(s.item.id);
  });
  const microcastSections = program.filterSections?.('microcast') ?? [];
  microcastSections.forEach((wrap: any) => {
    const m = wrap?.item?.microcast;
    collectQuizSectionIdsFromSections(m?.sections).forEach((id) => allowed.add(id));
  });
  return allowed;
}

/**
 * Resolve question metadata for display (same role as Course.getQuestionById).
 */
export function getQuestionForItemProgressQuiz(
  item: any,
  sectionID: string,
  questionId: string,
): any {
  try {
    if (item?.course) {
      return item.course.getQuestionById?.(sectionID, questionId) ?? null;
    }
    if (item?.program) {
      const course = findCourseInProgramForQuizKey(item.program, sectionID);
      if (course) {
        return course.getQuestionById?.(sectionID, questionId) ?? null;
      }
      const pq = findProgramQuizSection(item.program, sectionID);
      const fromProg = getQuestionFromProgramQuizSection(pq, questionId);
      if (fromProg) return fromProg;
      const microSections = item.program.filterSections?.('microcast') ?? [];
      for (const wrap of microSections) {
        const m = wrap?.item?.microcast;
        const sec = findQuizSectionInNestedSections(m?.sections, sectionID);
        const questions = sec?.item?.questions;
        if (Array.isArray(questions)) {
          const hit = questions.find((q: any) => q?.id === questionId);
          if (hit) return hit;
        }
      }
      return null;
    }
    if (item?.microcast) {
      const sec = findQuizSectionInNestedSections(item.microcast?.sections, sectionID);
      const questions = sec?.item?.questions;
      if (Array.isArray(questions)) {
        return questions.find((q: any) => q?.id === questionId) ?? null;
      }
    }
  } catch {
    return null;
  }
  return null;
}
